import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import type { CountryCode } from 'libphonenumber-js';

// Country code to flag emoji mapping
const getFlagEmoji = (countryCode: string) => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

interface CountryPhoneInputProps {
  value?: string;
  onChange: (phoneNumber: string) => void;
  error?: string;
  disabled?: boolean;
}

export default function CountryPhoneInput({
  value = '',
  onChange,
  error,
  disabled = false,
}: CountryPhoneInputProps) {
  const countries = getCountries();

  // Find matching country from value
  const findCountryFromValue = (val: string): CountryCode => {
    if (!val || !val.startsWith('+')) return 'IN';

    for (const country of countries) {
      const code = getCountryCallingCode(country);
      if (val.startsWith(`+${code}`)) {
        return country;
      }
    }
    return 'IN';
  };

  const selectedCountry = findCountryFromValue(value);
  const countryCode = getCountryCallingCode(selectedCountry);

  const phoneNumber = value.startsWith(`+${countryCode}`)
    ? value.slice(countryCode.length + 1)
    : '';

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = e.target.value as CountryCode;
    const newCode = getCountryCallingCode(country);
    onChange(`+${newCode}${phoneNumber}`);
  };

  const handlePhoneChange = (number: string) => {
    const cleaned = number.replace(/\D/g, '');
    onChange(`+${countryCode}${cleaned}`);
  };

  return (
    <div className="w-full">
      <div className="flex gap-2">
        {/* Country Select */}
        <div className="form-control w-48 shrink-0">
          <label className="label py-1">
            <span className="label-text text-xs">Country</span>
          </label>
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            disabled={disabled}
            className="select select-bordered select-sm h-10 min-h-10"
          >
            {countries.map((country) => {
              const flag = getFlagEmoji(country);
              const code = getCountryCallingCode(country);
              const name = en[country];
              return (
                <option key={country} value={country}>
                  {flag} {name} (+{code})
                </option>
              );
            })}
          </select>
        </div>

        {/* Phone Number Input */}
        <div className="form-control flex-1">
          <label className="label py-1">
            <span className="label-text text-xs">Phone Number</span>
          </label>
          <input
            type="tel"
            inputMode="numeric"
            placeholder="Enter your number"
            value={phoneNumber}
            onChange={(e) => handlePhoneChange(e.target.value)}
            disabled={disabled}
            className={`input input-bordered input-sm h-10 min-h-10 w-full ${error ? 'input-error' : ''}`}
          />
        </div>
      </div>

      {error && (
        <label className="label">
          <span className="label-text-alt text-error">{error}</span>
        </label>
      )}
    </div>
  );
}
